import React, { useCallback, useMemo, useState } from "react";
import moment from "moment";
import { Spin, Popover, Row, Col, Badge } from "antd";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList, getClientid } from "redux/selectors";
import { CALL_API } from "common/API";
import COLORS from "common/Colors";
import { StyledLeaveDetailsPopover } from "pages/Calendar/StyledComponents";

const FC_PLUGINS = [timeGridPlugin, dayGridPlugin, interactionPlugin];
const FC_HIDDEN_DAYS = [0];
const FC_HEADER_TOOLBAR = {
  left: "prev,next",
  center: "title",
  right: "timeGridWeek,dayGridMonth",
};

const textStyle = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const getWFHColor = (statusId) => {
  switch (statusId) {
    case "1": return "#E2FBCD";
    case "2": return "#FFCCCB";
    default: return "#FBE2CD";
  }
};

const getWFHBorderColor = (statusId) => {
  switch (statusId) {
    case "1": return "#78F138";
    case "2": return "#F13838";
    default: return "#F18D38";
  }
};

const getWFHStatusLabel = (statusId) => {
  switch (statusId) {
    case "1": return "Approved";
    case "2": return "Rejected";
    default: return "Pending";
  }
};

function WFHEventPopover({ info, userName }) {
  const [open, setOpen] = useState(false);
  const { statusId, reason, noOfDays, startDate, endDate,ownerName } =
    info.event.extendedProps;
  const backgroundColor = getWFHColor(statusId);
  const borderColor = getWFHBorderColor(statusId);
  const statusLabel = getWFHStatusLabel(statusId);
  const badgeColor =
    statusId === "1" ? COLORS.APPROVED_LEAVE : COLORS.PENDING_LEAVE;

  const startTime = info.event.start
    ? moment(info.event.start).format("hh:mm A")
    : "N/A";
  const endTime = info.event.end
    ? moment(info.event.end).format("hh:mm A")
    : "N/A";

  const popoverContent = (
    <StyledLeaveDetailsPopover>
      <Row>
        <Col span={24}>
          <h5>
            <Badge status="success" size="small" color={badgeColor} />{" "}
            {ownerName || userName || "N/A"}
          </h5>
        </Col>
      </Row>
      <Row gutter={20}>
        <Col span={8}>From date:</Col>
        <Col span={16}>{moment(startDate).format("Do MMM YYYY")}</Col>
      </Row>
      <Row gutter={20}>
        <Col span={8}>To date:</Col>
        <Col span={16}>{moment(endDate).format("Do MMM YYYY")}</Col>
      </Row>
      <Row gutter={20}>
        <Col span={8}>No. of days:</Col>
        <Col span={16}>{noOfDays}</Col>
      </Row>
      <Row gutter={20}>
        <Col span={8}>Status:</Col>
        <Col span={16}>{statusLabel}</Col>
      </Row>
      <Row gutter={20}>
        <Col span={8}>Reason:</Col>
        <Col span={16}>{reason ?? "-"}</Col>
      </Row>
    </StyledLeaveDetailsPopover>
  );

  return (
    <Popover
      content={popoverContent}
      placement="rightTop"
      trigger="hover"
      open={open}
      onOpenChange={setOpen}
    >
      <div
        style={{
          padding: "5px",
          background: backgroundColor,
          border: `1px solid ${borderColor}`,
          color: "#000",
          height: "100%",
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <span style={textStyle}>{info.event.title}</span>
        <p style={textStyle}>
          {startTime} - {endTime}
        </p>
      </div>
    </Popover>
  );
}

function WFHWeekCalendar({ activeTab = "Mine" }) {
  const userInfo = useSelector(getUserInfo);
  const userList = useSelector(getUserList);
  const ClientId = useSelector(getClientid);

  const [wfhData, setWfhData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const currentUser = useMemo(
    () => userList?.find((item) => item?.userid === userInfo?.userid),
    [userList, userInfo?.userid]
  );
  const userName = useMemo(
    () => currentUser?.fullname || userInfo?.fullname || userInfo?.userid || "",
    [currentUser, userInfo]
  );

  const fetchWFH = useCallback(
    async (start, end) => {
      if (!ClientId || !userInfo?.userid) return;
      const managingoffice = currentUser?.managingoffice;
      const country = Array.isArray(managingoffice)
        ? managingoffice[0]
        : managingoffice || "";

      const dateRange = {
        startDate: moment(start).format("YYYY-MM-DD"),
        endDate: moment(end).format("YYYY-MM-DD"),
      };

      let payload;
      if (activeTab === "My Team") {
        payload = {
          ...dateRange,
          requestType: "myteam",
          country,
          officesupervisorid: currentUser?.employeeid || "",
          statusId: "0,1",
        };
      } else if (activeTab === "All") {
        payload = {
          ...dateRange,
          requestType: "all",
        //   officesupervisorid: "",
          statusId: "0,1",
        };
      } else {
        payload = {
          ...dateRange,
          country,
          requestType: "mine",
          userId: userInfo.userid,
        };
      }

      setIsLoading(true);
      try {
        const data = await CALL_API("wfh/getwfhrequest", "post", payload);
        setWfhData(Array.isArray(data) ? data : []);
      } catch {
        setWfhData([]);
      } finally {
        setIsLoading(false);
      }
    },
    [ClientId, userInfo, currentUser, activeTab]
  );

  const handleDatesSet = useCallback(
    (arg) => {
      fetchWFH(arg.start, arg.end);
    },
    [fetchWFH]
  );

  const events = useMemo(() => {
    if (!wfhData?.length) return [];
    return wfhData.map((item) => {
      const ownerName =
        activeTab === "Mine"
          ? userName
          : (
          userList?.find(
            (user) => user?.userid === (item?.userid || item?.userId)
          )?.fullname ||
          item?.fullname ||
          item?.employeename ||
          item?.userId
        );
      return {
        id: `wfh-${item.wfhId}`,
        title: activeTab === "Mine" ? "Work From Home" : `${ownerName} | WFH`,
        start: moment(item.startDate).set({ hour: 8, minute: 30 }).toISOString(),
        end: moment(item.endDate)
          .set({ hour: Number(item.noOfDays) === 0.5 ? 12 : 17, minute: 30 })
          .toISOString(),
        extendedProps: {
          statusId: item.statusId,
          reason: item.reason,
          noOfDays: item.noOfDays,
          wfhId: item.wfhId,
          startDate: item.startDate,
          endDate: item.endDate,
          ownerName,
        },
      };
    });
  }, [wfhData, activeTab, userName]);

  const renderEventContent = useCallback(
    (info) => <WFHEventPopover info={info} userName={userName} />,
    [userName]
  );

  return (
    <Spin spinning={isLoading}>
      <FullCalendar
        plugins={FC_PLUGINS}
        height="auto"
        contentHeight="auto"
        initialView="timeGridWeek"
        events={events}
        headerToolbar={FC_HEADER_TOOLBAR}
        datesSet={handleDatesSet}
        editable={false}
        selectable={false}
        allDaySlot={false}
        eventOverlap={false}
        slotEventOverlap={true}
        slotMinTime="08:00:00"
        slotMaxTime="18:00:00"
        hiddenDays={FC_HIDDEN_DAYS}
        eventContent={renderEventContent}
      />
    </Spin>
  );
}

export default WFHWeekCalendar;
